"use client";

import { notifications } from "@mantine/notifications";
import { ProductPromotionForm } from "Components/Admin/AdminPromotion/AdminProduct/ProductForm";
import { ProductPromotionTable } from "Components/Admin/AdminPromotion/AdminProduct/ProductTable";
import { Card } from "Components/Admin/StyleComponent/Card";
import { Header } from "Components/Admin/StyleComponent/Header";
import { useEffect, useState } from "react";
import {
  useAddProductPromotionMutation,
  useDeleteProductPromotionMutation,
  useGetAllProductPromotionsQuery,
  useGetProductPromotionByIdQuery,
  useUpdateProductPromotionMutation,
} from "Services/Admin/ProductPromotionApiSlice";
import {
  AddProductPromotionPayload,
  ProductPromotion,
} from "Types/Admin/AdminProductPromotionType";
import { useEcomConfig } from "hooks/useEcomConfig";

const PLANT = process.env.REACT_APP_PLANT;

const AdminPromotionProduct = () => {
  const [showForm, setShowForm] = useState(false);
  const [showBulkForm, setShowBulkForm] = useState(false);
  const [editingPromotionProduct, setEditingPromotionProduct] =
    useState<ProductPromotion | null>(null);
  const [isViewMode, setIsViewMode] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(100);
  const [selectAll, setSelectAll] = useState(false);

  const { showPromotion } = useEcomConfig();
  const {
    data: promotionsData,
    isLoading,
    isError,
    refetch,
  } = useGetAllProductPromotionsQuery(
    {
      plant: PLANT,
    },
    { skip: !showPromotion },
  );

  const [deleteProductPromotion] = useDeleteProductPromotionMutation();
  const [addProductPromotion, { isLoading: isAdding }] =
    useAddProductPromotionMutation();

  const [updateProductPromotion, { isLoading: isUpdating }] =
    useUpdateProductPromotionMutation();

  const formLoading = isAdding || isUpdating;
  const { data: singlePromotion, isFetching: isFetchingSingle } =
    useGetProductPromotionByIdQuery(
      { id: editingPromotionProduct?.promotionHdr.id || 0, plant: PLANT },
      { skip: !editingPromotionProduct?.promotionHdr.id },
    );

  useEffect(() => {
    if (singlePromotion && editingPromotionProduct) {
      setEditingPromotionProduct(singlePromotion.results);
    }
  }, [singlePromotion]);

  const handleSelectAll = () => {
    setSelectAll(!selectAll);
  };

  const handleEdit = (promotion: ProductPromotion) => {
    setEditingPromotionProduct(promotion);
    setIsViewMode(false);
    setShowForm(true);
  };

  const handleView = (promotion: ProductPromotion) => {
    setEditingPromotionProduct(promotion);
    setIsViewMode(true);
    setShowForm(true);
  };

  const handleDelete = async (promotion: ProductPromotion) => {
    try {
      const res = await deleteProductPromotion({
        id: promotion.promotionHdr.id,
        plant: PLANT,
      }).unwrap();

      notifications.show({
        message: res.message,
        color: "red",
      });

      refetch();
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });
    }
  };

  const handleSubmit = async (data: AddProductPromotionPayload) => {
    try {
      if (editingPromotionProduct && !isViewMode) {
        const result = await updateProductPromotion({
          ...data,
          promotionHdr: {
            ...data.promotionHdr,
            id: editingPromotionProduct.promotionHdr.id,
          },
        }).unwrap();

        notifications.show({
          message: result.message,
          color: "green",
        });

        return result;
      }

      if (!isViewMode) {
        const result = await addProductPromotion(data).unwrap();

        notifications.show({
          message: result.message,
          color: "green",
        });

        return result;
      }
    } catch (error: any) {
      notifications.show({
        message: error?.data?.message || "An error occurred",
        color: "red",
      });

      throw error; // important so form keeps loader until finished
    } finally {
      setShowForm(false);
      setEditingPromotionProduct(null);
      refetch();
    }
  };

  const transformPromotionsToTableData = (
    promotions: ProductPromotion[] | undefined,
  ) => {
    if (!promotions) return [];

    return promotions.map((promotion) => ({
      id: promotion.promotionHdr.id,
      outlet: promotion.promotionHdr.outlet,
      promotion: promotion.promotionHdr.promotionName,
      promotion_description: promotion.promotionHdr.promotionDesc,
      start_date_or_time: `${promotion.promotionHdr.startDate} - ${promotion.promotionHdr.startTime}`,
      end_date_or_time: `${promotion.promotionHdr.endDate} - ${promotion.promotionHdr.endTime}`,
      isActive: promotion.promotionHdr.isActive,
      originalData: promotion,
    }));
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <div className="w-10 h-10 border-4 border-orange-400 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="flex items-center justify-center min-h-[60vh] text-red-500">
        Failed to load product promotions.
      </div>
    );
  }

  return (
    <div className="font-body p-6 mx-auto">
      {!showForm && !showBulkForm ? (
        <>
          <div className="flex flex-col md:flex-row justify-between items-center mb-6">
            <Header title="Product Promotion" />

            <button
              className="px-4 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700"
              onClick={() => {
                setEditingPromotionProduct(null);
                setIsViewMode(false);
                setShowForm(true);
              }}
            >
              Add Product Promotion
            </button>
          </div>

          <Card>
            <ProductPromotionTable
              promotions={transformPromotionsToTableData(promotionsData)}
              onEdit={(promotion) => handleEdit(promotion.originalData)}
              onView={(promotion) => handleView(promotion.originalData)}
              onDelete={(promotion) => handleDelete(promotion.originalData)}
              selectAll={selectAll}
              onSelectAll={handleSelectAll}
              currentPage={currentPage}
              itemsPerPage={itemsPerPage}
              onPageChange={setCurrentPage}
              onItemsPerPageChange={setItemsPerPage}
              isLoading={isLoading}
            />
          </Card>
        </>
      ) : showBulkForm ? (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => setShowBulkForm(false)}
          >
            ← Back
          </button>

          <Header title="Add Bulk Promotion" />
        </>
      ) : (
        <>
          <button
            className="flex items-center gap-1 text-gray-500 hover:text-gray-700 mb-4"
            onClick={() => {
              setShowForm(false);
              setEditingPromotionProduct(null);
            }}
          >
            ← Back
          </button>

          <Header
            title={
              isViewMode
                ? "View Product Promotion"
                : editingPromotionProduct
                  ? "Edit Product Promotion"
                  : "Add Product Promotion"
            }
          />

          <ProductPromotionForm
            initialValues={
              editingPromotionProduct
                ? {
                    ...editingPromotionProduct,
                    promotionHdr: {
                      ...editingPromotionProduct.promotionHdr,
                      crAt: editingPromotionProduct.promotionHdr.crAt || "",
                      crBy: editingPromotionProduct.promotionHdr.crBy || "",
                      upAt: editingPromotionProduct.promotionHdr.upAt || "",
                      upBy: editingPromotionProduct.promotionHdr.upBy || "",
                    },
                    promotionDet: editingPromotionProduct.promotionDet.map(
                      (det) => ({
                        ...det,
                        crAt: det.crAt || "",
                        crBy: det.crBy || "",
                        upAt: det.upAt || "",
                        upBy: det.upBy || "",
                        usageUsed: det.usageUsed || 0,
                      }),
                    ),
                  }
                : {
                    promotionHdr: {
                      byValue: 0,
                      crAt: "",
                      crBy: "",
                      customerTypeId: "",
                      endDate: "",
                      endTime: "",
                      id: 0,
                      isActive: "Y",
                      notes: "",
                      outlet: "",
                      plant: PLANT,
                      promotionDesc: "",
                      promotionName: "",
                      startDate: "",
                      startTime: "",
                      upAt: "",
                      upBy: "",
                    },
                    promotionDet: [
                      {
                        buyItem: "",
                        buyQty: 0,
                        crAt: "",
                        crBy: "",
                        getItem: "",
                        getQty: 0,
                        hdrId: 0,
                        id: 0,
                        limitOfUsage: 0,
                        lnNo: 1,
                        plant: PLANT,
                        promotion: 0,
                        promotionType: "%",
                        upAt: "",
                        upBy: "",
                        usageUsed: 0,
                      },
                    ],
                  }
            }
            onSubmit={handleSubmit}
            onCancel={() => {
              setShowForm(false);
              setEditingPromotionProduct(null);
            }}
            isViewMode={isViewMode}
            loading={formLoading}
          />
        </>
      )}
    </div>
  );
};

export default AdminPromotionProduct;
